import { useMemo } from 'react'
import { Circle, RefreshCw, PlayCircle, GitCommitHorizontal, Code, GitBranch } from 'lucide-react'
import { parse as parseYaml } from 'yaml'
import {
  CodeBlocksIcon,
  GitCloneIcon,
  GitPullRequestCreateIcon,
  TextSnippetIcon,
  EditPenIcon,
  ValidateConnectorIcon,
  NotificationsIcon,
  NetworkNodeIcon,
  TransformDataIcon,
  AutomationIcon,
  DataObjectIcon,
  ActionKeyIcon,
} from '@/components/icons'

interface YamlStructureTreeProps {
  yaml: string
}

interface ParsedBranch {
  name: string
  nodes: ParsedNode[]
}

interface ParsedPhase {
  name: string
  gate?: boolean
  approvalRoles?: string[]
  branches: ParsedBranch[]
}

interface ParsedNode {
  name: string
  type: string
  hasRetry: boolean
}

const NODE_TYPE_ICONS: Record<string, React.ComponentType<{ className?: string }>> = {
  'ai-claude-code': CodeBlocksIcon,
  'claude-code': CodeBlocksIcon,
  'devops-run-command': PlayCircle,
  'run-command': PlayCircle,
  'devops-git-clone': GitCloneIcon,
  'git-clone': GitCloneIcon,
  'devops-git-commit-push': GitCommitHorizontal,
  'git-commit-push': GitCommitHorizontal,
  'devops-create-pr': GitPullRequestCreateIcon,
  'create-pull-request': GitPullRequestCreateIcon,
  'pm-fetch-work-item': TextSnippetIcon,
  'fetch-work-item': TextSnippetIcon,
  'create-change-set': EditPenIcon,
  'apply-change-set': GitCommitHorizontal,
  'utility-validate-connector': ValidateConnectorIcon,
  'validate-connector': ValidateConnectorIcon,
  'utility-send-notification': NotificationsIcon,
  'send-notification': NotificationsIcon,
  'utility-evaluate-conditions': NetworkNodeIcon,
  'evaluate-conditions': NetworkNodeIcon,
  'utility-transform': TransformDataIcon,
  'transform-data': TransformDataIcon,
  'prompt-implement-work-item': AutomationIcon,
  'implement-work-item': AutomationIcon,
  'prompt-code-review': DataObjectIcon,
  'code-review': DataObjectIcon,
  'prompt-generate-tests': ActionKeyIcon,
  'generate-tests': ActionKeyIcon,
  'prompt-deployment-checklist': Code,
  'deployment-checklist': Code,
  'agent-analyze': CodeBlocksIcon,
  'agent-generate': CodeBlocksIcon,
  'ai-review': DataObjectIcon,
  'devops-test': PlayCircle,
  'devops-deploy': GitCommitHorizontal,
}

function extractStructure(yamlContent: string): ParsedPhase[] {
  if (!yamlContent.trim()) return []
  try {
    const parsed = parseYaml(yamlContent)
    if (!parsed?.phases || !Array.isArray(parsed.phases)) return []

    return parsed.phases.map((phase: Record<string, unknown>) => {
      const branches: ParsedBranch[] = []
      if (Array.isArray(phase.branches)) {
        for (const branch of phase.branches as Record<string, unknown>[]) {
          const nodes: ParsedNode[] = []
          if (Array.isArray(branch.nodes)) {
            for (const node of branch.nodes as Record<string, unknown>[]) {
              nodes.push({
                name: String(node.name ?? 'unnamed'),
                type: String(node.type ?? 'unknown'),
                hasRetry: !!node.retryStrategy,
              })
            }
          }
          branches.push({ name: String(branch.name ?? 'unnamed'), nodes })
        }
      }
      return {
        name: String(phase.name ?? 'unnamed'),
        gate: !!phase.requiresApproval,
        approvalRoles: Array.isArray(phase.approvalRoles) ? phase.approvalRoles.map(String) : undefined,
        branches,
      }
    })
  } catch {
    return []
  }
}

export function YamlStructureTree({ yaml }: YamlStructureTreeProps) {
  const phases = useMemo(() => extractStructure(yaml), [yaml])

  return (
    <div className="px-4 pb-4" data-testid="yaml-structure-tree">
      <h4 className="text-xs font-bold uppercase leading-[1.6] tracking-[0.015em] text-[rgba(145,149,167,1)] mb-3">
        Structure
      </h4>
      {phases.length === 0 ? (
        <p className="text-sm text-foreground">
          {yaml.trim() ? 'No valid structure found' : 'Enter YAML to see structure'}
        </p>
      ) : (
        <div className="space-y-2">
          {phases.map((phase, i) => (
            <PhaseCard key={`${phase.name}-${i}`} phase={phase} />
          ))}
        </div>
      )}
    </div>
  )
}

function PhaseCard({ phase }: { phase: ParsedPhase }) {
  return (
    <div className="rounded-lg border-l-[3px] border border-button bg-card overflow-hidden">
      <div className="flex items-center justify-between px-3 pt-2.5 pb-1">
        <span className="text-sm font-semibold capitalize text-button">
          {phase.name}
        </span>
        {phase.gate && (
          <span className="text-[10px] font-medium border border-muted-foreground text-muted-foreground rounded px-1.5 py-0.5 leading-none">
            Gate
          </span>
        )}
      </div>
      {phase.approvalRoles && phase.approvalRoles.length > 0 && (
        <div className="px-3 pb-1">
          <span className="text-[10px] text-muted-foreground">
            Roles: {phase.approvalRoles.join(', ')}
          </span>
        </div>
      )}
      {phase.branches.length > 0 && (
        <div className="px-3 pb-2">
          {phase.branches.map((branch, bi) => (
            <div key={`${branch.name}-${bi}`}>
              <div className="flex items-center gap-1.5 py-1">
                <GitBranch className="h-3 w-3 shrink-0 text-muted-foreground" />
                <span className="text-xs font-medium capitalize text-foreground">
                  {branch.name}
                </span>
              </div>
              {branch.nodes.map((node, ni) => {
                const NodeIcon = NODE_TYPE_ICONS[node.type] ?? Circle
                return (
                  <div
                    key={`${node.name}-${ni}`}
                    className="flex items-center gap-2 py-1 pl-4 text-sm"
                  >
                    <NodeIcon className="h-3.5 w-3.5 shrink-0 text-foreground" />
                    <span className="flex-1 truncate text-foreground">{node.name}</span>
                    {node.hasRetry && (
                      <RefreshCw className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                    )}
                  </div>
                )
              })}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
